import {
  compileCustomDisciplineField,
  evaluateCustomDisciplineCell,
  evaluateCustomDisciplineActionClick,
  parseCustomDisciplineBehavior,
  releaseCustomDisciplineBehaviorRuntime,
  type CustomDisciplineEvaluateInput,
} from "../custom-discipline-behavior/index.js";
import type {
  CustomDisciplineFieldDefinition,
  CustomDisciplineGroupType,
} from "./types.js";

export type CustomDisciplineIntegrationMap = Record<
  string,
  string | { field: string } | { value: unknown }
>;

/** Optional compatibility projection, defined by an ordinary administrator action. */
export function projectCustomDisciplineCompetition(
  schema: Pick<CustomDisciplineGroupType, "fields" | "actions">,
  input: CustomDisciplineEvaluateInput,
) {
  const action = schema.actions.find(
    (item) => item.key === "competition-projection" && item.isActive,
  );
  if (!action?.behavior) return null;
  const result = evaluateCustomDisciplineActionClick(
    compileCustomDisciplineField(
      `competition:${action.id ?? action.key}`,
      parseCustomDisciplineBehavior(action.behavior)!,
    ),
    { ...input, instanceKey: `${input.instanceKey}:competition` },
  );
  const definition = result.commands.find(
    (item) =>
      item.action === "customGroupService" &&
      (item.payload as { service?: string })?.service === "competition",
  )?.payload as
    | {
        format?: string;
        fieldMap?: CustomDisciplineIntegrationMap;
      }
    | undefined;
  if (!definition?.fieldMap) return null;
  if (!["FULL_POWER", "SINGLE_LIFT"].includes(definition.format ?? ""))
    throw new Error(
      "Le format de l’adaptateur de compétition n’est pas pris en charge",
    );
  return {
    format: definition.format!,
    athletes: projectCustomDisciplineIntegrationRows(
      schema.fields.filter((field) => field.scope === "ATHLETE"),
      input,
      definition.fieldMap,
    ),
  };
}

/** Adapter inputs use the same field Behavior as the competition UI. */
export function projectCustomDisciplineIntegrationRows(
  fields: CustomDisciplineFieldDefinition[],
  input: CustomDisciplineEvaluateInput,
  mapping: CustomDisciplineIntegrationMap,
): Record<string, unknown>[] {
  const compiled = new Map(
    fields
      .filter((field) => field.behavior)
      .map((field) => [
        field.key,
        compileCustomDisciplineField(
          `integration:${field.id ?? field.key}`,
          parseCustomDisciplineBehavior(field.behavior)!,
        ),
      ]),
  );
  // Export cells remain fresh, while pure subexpressions about the same roster
  // and categories can be reused across rows during this projection only.
  const evaluationCache = { byContext: new WeakMap(), batch: true };
  return input.athletes.map((athlete, index) => {
    const row: Record<string, unknown> = { id: athlete.id };
    for (const [target, rule] of Object.entries(mapping)) {
      if (typeof rule === "object" && rule && "value" in rule) {
        row[target] = rule.value;
        continue;
      }
      const key =
        typeof rule === "string"
          ? rule
          : "field" in rule
            ? rule.field
            : undefined;
      const field = fields.find((item) => item.key === key);
      if (!field) throw new Error(`Champ d’intégration introuvable : ${key}`);
      let value: unknown = athlete[field.key];
      const behavior = compiled.get(field.key);
      if (behavior) {
        // Exports are fresh evaluations, not mounted UI cells. Never retain a
        // roster's event signatures or reuse onLoad results in a later export.
        const instanceKey = `${input.instanceKey}:integration:${athlete.id ?? index}:${field.key}`;
        releaseCustomDisciplineBehaviorRuntime(instanceKey);
        try {
          value = evaluateCustomDisciplineCell(behavior, {
            ...input,
            evaluationCache,
            athlete,
            defaultText: value == null ? "" : String(value),
            instanceKey,
          }).text;
        } finally {
          releaseCustomDisciplineBehaviorRuntime(instanceKey);
        }
      }
      if (field.type === "BOOLEAN") value = value === true || value === "true";
      else if (["NUMBER", "LIFT"].includes(field.type))
        value = value == null || value === "" ? null : Number(value);
      else if (
        field.type === "FOREIGN_KEY" &&
        value &&
        typeof value === "object"
      )
        value = (value as Record<string, unknown>).id;
      row[target] = value ?? null;
    }
    return row;
  });
}
